import type { FaqId } from "./faq";
import {
  BAND_PRICE,
  FREE_LIMIT,
  MILLION,
  EXAMPLE_SUBSCRIBERS,
  EXAMPLE_ISSUES_PER_MONTH,
  figure,
} from "./pricing";

/**
 * The three bands, spelled once. The landing teaser and the pricing page both
 * render this array, and `content.test.ts` checks each `price`/`detail`
 * against `pricing.ts`'s maths, so a band change is still a one-file edit.
 *
 * `id` exists to key the list. Keying on `price` was the same string that
 * renders, and two bands priced "$10" (the model already has "$10/mo" and
 * "+$10") would collide into one React key (#792 review).
 */
export const BANDS = [
  {
    id: "free",
    price: "Free",
    detail: `up to ${figure(FREE_LIMIT)} emails a month`,
  },
  {
    id: "million",
    price: `$${BAND_PRICE}/mo`,
    detail: `up to ${figure(MILLION)}`,
  },
  { id: "beyond", price: `+$${BAND_PRICE}`, detail: "per million after that" },
] as const;

export const PRICING_PAGE = {
  title: "Your blog is included.",
  titleAccent: "Pay as you send.",
  eyebrow: "Simple, send-based pricing",
  allowance: "Monthly email allowance",
  freeLabel: "Free",
  freeNote: "Blog, newsletter, and publishing tools included for writers, experts, and teams. No credit card required.",
  allowanceNote: "Emails per month, not subscribers on your list.",
  paidLabel: "When your newsletter grows",
  firstPaid: `From ${figure(FREE_LIMIT + 1)} to ${figure(MILLION)} emails`,
  nextPaid: `Each additional started ${figure(MILLION)} emails`,
  boundaryNote: `At ${figure(FREE_LIMIT)} emails you pay $0. At ${figure(FREE_LIMIT + 1)}, the monthly band is $${BAND_PRICE}. Above ${figure(MILLION)}, each started band adds $${BAND_PRICE}; partial bands are not prorated.`,
  currency: "All prices in USD per month.",
  includedLink: "See everything included",
  compareNote: "Comparing GoodSender with other blog and newsletter platforms?",
  compareLink: "See the side-by-side comparisons",
  examplesTitle: "What does that look like?",
  examplesLede:
    "One newsletter to one subscriber counts as one email. These examples assume four issues per month.",
  examplesLabels: [
    "Emma’s travel journal",
    "A growing community",
    "An established publication",
    "A widely read newsletter",
  ],
  exampleSubscribers: EXAMPLE_SUBSCRIBERS,
  examplesCadence: EXAMPLE_ISSUES_PER_MONTH,
  subscribersUnit: "subscribers",
  sendsUnit: "emails / month",
  calculatorTitle: "Find your monthly band.",
  calculatorLede:
    "Change your audience size and sending cadence. This estimates newsletter sends; include any other email you send when planning your total volume.",
  comparisonTitle: "Compare illustrative email costs",
  lede: `Start with ${figure(FREE_LIMIT)} emails a month, free. Write on your own or with assistance, then grow your list. Charges begin only when your email volume crosses the free allowance.`,
  bands: BANDS,
  calculator: {
    subscribersLabel: "Subscribers",
    issuesLabel: "Issues per month",
    emailsLine: "emails a month",
    yoursLabel: "GoodSender",
    perSubLabel: "Typical per-subscriber platform",
    sesLabel: "Amazon SES",
    perMonth: "a month",
    /*
     * The qualifier on the two competitor figures, rendered by the calculator
     * itself rather than left to the page's footnote. A comparative price
     * claim and the sentence that qualifies it must not be separable by a
     * layout change (#792 review).
     */
    disclaimer: "Competitor figures are illustrative and change often.",
  },
  examples: [
    "10,000 subscribers, weekly: free.",
    "30,000 subscribers, four issues a month: $10 a month.",
    "100,000 subscribers, four issues a month: $10 a month, flat.",
    "500,000 subscribers, four issues a month: $20 a month.",
  ],
  /*
   * The footnote names two competitors and prices both, so it carries its own
   * qualifier. Attaching one to the calculator was right, but it was *moved*
   * rather than duplicated, and these two claims were left bare (#792 review).
   * A qualifier belongs with every set of comparative figures, not once per
   * page: the two blocks are independently placed, and the test below now
   * checks each surface that names a competitor rather than forbidding the
   * word here.
   */
  footnote:
    "For comparison, Amazon SES, the cheapest raw infrastructure, runs about $100 per million, and managed senders several hundred or more. Those figures are illustrative and change often. Your cost with us is based on what you send.",
  /*
   * Typed as `FaqId`, so an id that no entry defines is a `tsc` failure right
   * here rather than a card missing from /pricing (#799 review). Two guards
   * behind it, both older than that annotation: content.test.ts's "resolves
   * every id the pricing page names to an FAQ entry", and the `throw` in
   * routes/_home/pricing.tsx. The type is the one that fires without running
   * anything, which is why the id list carries it.
   *
   * The subset is the docs 03 one: cheaper than everyone, Ghost,
   * transactional. `engine` sat in the third slot only because the agent-first
   * strict cut (D-1, #799) had retired the `transactional` entry entirely; the
   * 2026-08-24 CEO review restored the entry, and #895 restored it here, where
   * docs 01 §10 wants the one-vendor switching hook next to the price.
   * `engine` remains on /faq.
   */
  faqIds: [
    "pricing",
    "ghost",
    "transactional",
  ] as const satisfies readonly FaqId[],
  // The heading over that subset. Here, not inlined in the route, so it is
  // covered by the voice tests in content.test.ts like every sibling string.
  faqTitle: "Pricing questions",

  /*
   * The assembled-stack cost comparison (CEO review, 2026-08-24), moved here
   * from the landing unchanged (AI first homepage, 2026-10-06): /pricing is
   * where a reader comparing bills already is, and it sits under the
   * calculator as the same argument at the scale of the whole stack.
   *
   * Categories, never vendor names. The landing names no competitor anywhere
   * today (Ghost appears only in an FAQ answer), and a named vendor's price
   * is a third party's number that moves without warning, so a table of them
   * would need a review cadence nobody owns. Categories also make the actual
   * argument better: the point is the sprawl, not any one bill.
   *
   * No figure lives in this object. `StackCost.tsx` formats every number from
   * `pricing.ts` (`STACK_COSTS`, `stackMonthly`, `goodsenderMonthly`), so a
   * price here cannot drift from the model the way a retyped string would.
   * `parts[].id` is what joins the two; content.test.ts pins that the two
   * lists name the same set.
   */
  stack: {
    eyebrow: "What it replaces",
    title: "The stack you don't have to buy",
    /*
     * The scenario figures here ("10,000 subscribers publishing weekly") are
     * prose, and they are the ONLY numbers in this block: everything in the
     * table is formatted from `pricing.ts` by `StackCost.tsx`. Prose that
     * quotes a figure is the `BANDS[].detail` pattern next door, and it gets
     * the same protection — content.test.ts reads this sentence back into
     * `STACK_SUBSCRIBERS` and `STACK_ISSUES_PER_MONTH`, so moving a constant
     * without rewriting the sentence fails rather than leaving the page
     * describing one customer while pricing another (#823 review).
     */
    lede: "Running this yourself means buying a place to publish, a sender priced on the size of your list, and a forms tool, then keeping the three wired together. Here is that bill at 10,000 subscribers publishing weekly.",
    parts: [
      { id: "cms", name: "Hosted blog or CMS" },
      { id: "esp", name: "Email service provider" },
      { id: "forms", name: "Subscribe forms and popups" },
    ],
    /*
     * The exclusions, stated on the page rather than left silent. Two rows
     * were charged to the assembled stack alone and were not the stack's to
     * carry (#823 review): a domain (GoodSender has no subdomains of its own,
     * so it is required either way) and web analytics (GoodSender ships no
     * page-view tracking, so a publisher supplies their own GA or GTM
     * property either way). The certificate is not shared at all, it is ours
     * to issue, which is why the last sentence gets to be a small claim
     * rather than an apology.
     *
     * The three rows above are exactly the three the lede names. Keep it that
     * way: a row the lede does not name is the shape both removed rows had.
     */
    sharedNote:
      "You bring your own domain either way, and your own web analytics tag, so neither column counts them. The certificate is on us.",
    /*
     * The line the table exists to set up. Deliberately not a cost row: the
     * integration work is the part a price comparison always leaves out, and
     * it is the part a reader recognises from their own week.
     */
    effortRow: "Wiring it together and keeping it wired",
    effortValue: "your afternoons",
    assembledLabel: "Assembled yourself",
    oursLabel: "GoodSender",
    perMonth: "a month",
    /*
     * A comparative price claim and the sentence qualifying it must not be
     * separable by a layout change, so this rides with the figures rather
     * than relying on the page footer (the rule content.test.ts enforces per
     * surface, #792 review). This surface names no vendor, but it still puts
     * a number on what other products cost.
     */
    disclaimer:
      "Category figures are illustrative and change often. Your cost with us is based on what you send.",
  },
} as const;
